import { supabase } from './supabase';

import { resolvePressureTemplateProfile, resolvePressureTemplateProfileByCode } from './pressure-pilot-profiles';

const SQRT3 = Math.sqrt(3);
export const DEFAULT_VALIDITY_MONTHS = 6;
const EXCEL_VALIDITY_DAYS_BY_MONTHS = {
  3: 90,
  6: 181,
  12: 365,
  24: 730,
};

function mean(values = []) {
  const xs = values.map(Number).filter((v) => Number.isFinite(v));
  if (!xs.length) return null;
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

function stdevSample(values = []) {
  const xs = values.map(Number).filter((v) => Number.isFinite(v));
  const n = xs.length;
  if (n < 2) return 0;
  const m = mean(xs);
  const variance = xs.reduce((acc, x) => acc + (x - m) ** 2, 0) / (n - 1);
  return Math.sqrt(variance);
}

function toNum(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function resolvePressureValidityDays(months = DEFAULT_VALIDITY_MONTHS) {
  const numericMonths = Number(months);
  if (!Number.isFinite(numericMonths) || numericMonths <= 0) return EXCEL_VALIDITY_DAYS_BY_MONTHS[DEFAULT_VALIDITY_MONTHS];
  if (EXCEL_VALIDITY_DAYS_BY_MONTHS[numericMonths]) return EXCEL_VALIDITY_DAYS_BY_MONTHS[numericMonths];
  return Math.round((365 / 12) * numericMonths);
}

export function addPressureValidityDaysToIsoDate(isoDate, months) {
  const base = String(isoDate || '').slice(0, 10);
  if (!base) return null;
  const days = resolvePressureValidityDays(months);
  const d = new Date(`${base}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function computeTemperatureCorrectionFactor({ ambientTemp = null, instrumentUrl = null } = {}) {
  const temp = Number(ambientTemp);
  const url = Math.abs(Number(instrumentUrl));
  if (!Number.isFinite(temp) || !Number.isFinite(url) || url <= 0) return 0;
  const deviation = Math.abs(temp - 20);
  return 0.004 * (deviation / 10) * url;
}

function getPointReading(rows = [], seriesNo, direction, key) {
  const match = (rows || []).find((r) => Number(r.series_no) === seriesNo && String(r.direction || '').toUpperCase() === direction);
  return match ? toNum(match[key], NaN) : NaN;
}

export function computePressurePointMetrics(rows = [], session = {}) {
  const pattern1Asc = getPointReading(rows, 1, 'ASC', 'pattern_reading');
  const pattern1Desc = getPointReading(rows, 1, 'DESC', 'pattern_reading');
  const pattern2Asc = getPointReading(rows, 2, 'ASC', 'pattern_reading');
  const pattern2Desc = getPointReading(rows, 2, 'DESC', 'pattern_reading');

  const instrument1Asc = getPointReading(rows, 1, 'ASC', 'instrument_reading');
  const instrument1Desc = getPointReading(rows, 1, 'DESC', 'instrument_reading');
  const instrument2Asc = getPointReading(rows, 2, 'ASC', 'instrument_reading');
  const instrument2Desc = getPointReading(rows, 2, 'DESC', 'instrument_reading');

  const patternVals = [pattern1Asc, pattern1Desc, pattern2Asc, pattern2Desc].filter(Number.isFinite);
  const instVals = [instrument1Asc, instrument1Desc, instrument2Asc, instrument2Desc].filter(Number.isFinite);
  if (!patternVals.length || !instVals.length) return null;

  const patternMean = mean(patternVals);
  const instrumentMean = mean(instVals);
  const hysteresisCandidates = [
    Math.abs(pattern1Desc - pattern1Asc),
    Math.abs(pattern2Desc - pattern2Asc),
  ].filter(Number.isFinite);
  const hysteresis = hysteresisCandidates.length ? Math.max(...hysteresisCandidates) : 0;
  const errorAbs = (instrumentMean ?? 0) - (patternMean ?? 0);
  const errorPct = patternMean ? (errorAbs / patternMean) * 100 : null;
  const repeatability = Math.max(
    stdevSample([pattern1Asc, pattern2Asc]),
    stdevSample([pattern1Desc, pattern2Desc])
  );

  const ambientTemp = session?.ambient_temp_avg ?? session?.ambient_temp ?? null;
  const instrumentUrl = session?.instrument_url ?? session?.instrument_url_value ?? session?.instrument_range_max ?? null;
  const tempCorrectionFactor = computeTemperatureCorrectionFactor({ ambientTemp, instrumentUrl });

  const uPatternCal = toNum(session.pattern_uncertainty_max, 0) / 2;
  const uPatternDrift = toNum(session.pattern_drift, 0) / (2 * SQRT3);
  const uPatternHysteresis = toNum(session.pattern_hysteresis, 0) / (2 * SQRT3);
  const uPatternResolution = toNum(session.pattern_resolution, 0) / (2 * SQRT3);
  const uInstrumentResolution = (toNum(session.instrument_resolution, 0) / 2) / (2 * SQRT3);
  const uInstrumentTempCorr = tempCorrectionFactor / (2 * SQRT3);
  const uHeightDifference = toNum(session.height_difference_m, 0) / (2 * SQRT3);
  const uRepeatability = toNum(repeatability, 0) / (2 * SQRT3);

  const uc = Math.sqrt(
    uPatternCal ** 2 +
      uPatternDrift ** 2 +
      uPatternHysteresis ** 2 +
      uPatternResolution ** 2 +
      uInstrumentResolution ** 2 +
      uInstrumentTempCorr ** 2 +
      uHeightDifference ** 2 +
      uRepeatability ** 2
  );
  const uExpanded = uc * 2;
  const uReported = Math.max(uExpanded, toNum(session.pattern_resolution, 0));

  return {
    pattern_mean: patternMean,
    instrument_mean: instrumentMean,
    hysteresis,
    error_abs: errorAbs,
    error_pct: errorPct,
    repeatability,
    temp_correction_factor: tempCorrectionFactor,
    u_pattern_cal: uPatternCal,
    u_pattern_drift: uPatternDrift,
    u_pattern_hysteresis: uPatternHysteresis,
    u_pattern_resolution: uPatternResolution,
    u_instrument_resolution: uInstrumentResolution,
    u_instrument_temp_corr: uInstrumentTempCorr,
    u_height_difference: uHeightDifference,
    u_repeatability: uRepeatability,
    uc,
    k: 2,
    u_expanded: uExpanded,
    u_reported: uReported,
  };
}
function sanitizePatch(patch = {}) {
  const out = {};
  Object.entries(patch || {}).forEach(([k, v]) => {
    if (v === undefined) return;
    if (typeof v === 'number' && !Number.isFinite(v)) return;
    out[k] = v;
  });
  return out;
}
async function getDefaultPressureReferenceStandard({ instrumentRangeMin = null, instrumentRangeMax = null } = {}) {
  try {
    let query = supabase
      .from('pressure_reference_standards')
      .select('*')
      .eq('active', true)
      .order('code', { ascending: true })
      .limit(1);

    if (Number.isFinite(Number(instrumentRangeMin))) {
      query = query.lte('range_min', Number(instrumentRangeMin));
    }
    if (Number.isFinite(Number(instrumentRangeMax))) {
      query = query.gte('range_max', Number(instrumentRangeMax));
    }

    let { data, error } = await query;
    if (error) throw error;
    if (data && data.length) return data[0];

    const fallback = await supabase
      .from('pressure_reference_standards')
      .select('*')
      .eq('active', true)
      .order('code', { ascending: true })
      .limit(1);

    if (fallback.error) throw fallback.error;
    return (fallback.data && fallback.data[0]) || null;
  } catch (err) {
    console.warn('[PressurePilot] standard lookup fallback', err);
    return null;
  }
}

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

async function getDailyEnvironmentLogByDate(logDate = todayIsoDate()) {
  try {
    const { data, error } = await supabase
      .from('daily_environment_log')
      .select('*')
      .eq('log_date', logDate)
      .maybeSingle();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    return data || null;
  } catch (err) {
    console.warn('[PressurePilot] daily environment lookup fallback', err);
    return null;
  }
}

export async function getTodayDailyEnvironmentLog() {
  return getDailyEnvironmentLogByDate(todayIsoDate());
}

export async function upsertTodayDailyEnvironmentLog({
  pressure_hpa = 0,
  temp_c = 0,
  humidity_rh = 0,
  pressure_unc_hpa = 1,
  temp_unc_c = 1,
  humidity_unc_rh = 3,
  notes = '',
} = {}) {
  const userId = (await supabase.auth.getUser())?.data?.user?.id ?? null;
  const payload = {
    log_date: todayIsoDate(),
    pressure_hpa: toNum(pressure_hpa, 0),
    temp_c: toNum(temp_c, 0),
    humidity_rh: toNum(humidity_rh, 0),
    pressure_unc_hpa: toNum(pressure_unc_hpa, 1),
    temp_unc_c: toNum(temp_unc_c, 1),
    humidity_unc_rh: toNum(humidity_unc_rh, 3),
    notes: notes || '',
    created_by: userId,
    updated_by: userId,
  };

  const { data, error } = await supabase
    .from('daily_environment_log')
    .upsert(payload, { onConflict: 'log_date' })
    .select('*')
    .single();

  if (error) throw error;
  return data;
}
export async function createPressureManometerSession({
  instrumentId,
  serviceOrderId = null,
  calibrationId = null,
  certificateType = 'NO_SAC',
  validityMonths = DEFAULT_VALIDITY_MONTHS,
  templateCode = null,
}) {
  if (!instrumentId) throw new Error('instrumentId es requerido');

  const {
    data: instrument,
    error: iErr,
  } = await supabase
    .from('instruments')
    .select(
      `
      id, tag, brand, model, serial, type, unit, resolution, range_min, range_max, tolerance,
      client:client_id ( id, client_number, name, address, locality, province )
      `
    )
    .eq('id', instrumentId)
    .single();

  if (iErr) throw iErr;

  const standard = await getDefaultPressureReferenceStandard({
    instrumentRangeMin: instrument?.range_min ?? null,
    instrumentRangeMax: instrument?.range_max ?? null,
  });
  const dailyEnv = await getDailyEnvironmentLogByDate();
  const templateProfile =
    resolvePressureTemplateProfileByCode(templateCode) ||
    resolvePressureTemplateProfile({
      instrumentLrl: instrument?.range_min ?? null,
      instrumentUrl: instrument?.range_max ?? null,
    });

  const payload = {
    instrument_id: instrument.id,
    service_order_id: serviceOrderId,
    calibration_id: calibrationId,
    certificate_type: certificateType,
    status: 'draft',
    template_code: templateProfile.templateCode,
    template_revision: templateProfile.templateRevision,

    client_number: instrument?.client?.client_number ? String(instrument.client.client_number) : null,
    client_name: instrument?.client?.name || null,
    client_address: instrument?.client?.address || null,
    client_city: instrument?.client?.locality || null,
    client_province: instrument?.client?.province || null,

    instrument_object: instrument?.type || 'pressure',
    instrument_brand: instrument?.brand || null,
    instrument_model: instrument?.model || null,
    instrument_serial: instrument?.serial || null,
    instrument_tag: instrument?.tag || null,
    instrument_resolution: instrument?.resolution ?? null,
    instrument_unit: instrument?.unit || 'bar',
    instrument_lrl: instrument?.range_min ?? null,
    instrument_url: instrument?.range_max ?? null,

    pressure_reference_unit: standard?.unit || instrument?.unit || 'bar',

    validity_months: Number(validityMonths) || DEFAULT_VALIDITY_MONTHS,
    ambient_pressure_avg: dailyEnv?.pressure_hpa ?? 0,
    ambient_temp_avg: dailyEnv?.temp_c ?? 0,
    ambient_humidity_avg: dailyEnv?.humidity_rh ?? 0,
    pattern_name: standard?.name || templateProfile.patternReferenceName || null,
    pattern_serial: standard?.serial || null,
    pattern_certificate: standard?.certificate_no || null,
    pattern_calibration_date: standard?.calibration_date || null,

    pattern_hysteresis: standard?.hysteresis_max ?? templateProfile.pattern_hysteresis,
    pattern_uncertainty_max: standard?.uncertainty_max ?? templateProfile.pattern_uncertainty_max,
    pattern_resolution: standard?.resolution ?? templateProfile.pattern_resolution,
    pattern_drift: standard?.drift ?? templateProfile.pattern_drift,
    temp_correction_factor: computeTemperatureCorrectionFactor({
      ambientTemp: dailyEnv?.temp_c ?? null,
      instrumentUrl: instrument?.range_max ?? null,
    }),
    height_difference_m: templateProfile.height_difference_m,
  };

  const { data, error } = await supabase
    .from('pressure_calibration_sessions')
    .insert(payload)
    .select('*')
    .single();

  if (error) throw error;
  return data;
}

export async function getPressureManometerSession(sessionId) {
  const { data, error } = await supabase
    .from('pressure_calibration_sessions')
    .select('*')
    .eq('id', sessionId)
    .single();
  if (error) throw error;
  return data;
}

export async function updatePressureManometerSession(sessionId, patch) {
  const safePatch = sanitizePatch(patch);
  const { data, error } = await supabase
    .from('pressure_calibration_sessions')
    .update({ ...safePatch, updated_by: (await supabase.auth.getUser())?.data?.user?.id ?? null })
    .eq('id', sessionId)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function listPressureManometerSessionsByInstrument(instrumentId) {
  const { data, error } = await supabase
    .from('pressure_calibration_sessions')
    .select('*')
    .eq('instrument_id', instrumentId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function savePressureManometerPoints(sessionId, rows) {
  if (!sessionId) throw new Error('sessionId es requerido');

  const payload = [];
  (rows || []).forEach((r) => {
    const pointNo = Number(r.point_no);
    const setpoint = toNum(r.setpoint, NaN);
    const unit = r.unit || 'bar';
    if (!Number.isFinite(pointNo) || !Number.isFinite(setpoint)) return;

    payload.push(
      {
        session_id: sessionId,
        point_no: pointNo,
        series_no: 1,
        direction: 'ASC',
        setpoint,
        pattern_reading: toNum(r.s1_asc_pattern, NaN),
        instrument_reading: toNum(r.s1_asc_instrument, NaN),
        unit,
      },
      {
        session_id: sessionId,
        point_no: pointNo,
        series_no: 1,
        direction: 'DESC',
        setpoint,
        pattern_reading: toNum(r.s1_desc_pattern, NaN),
        instrument_reading: toNum(r.s1_desc_instrument, NaN),
        unit,
      },
      {
        session_id: sessionId,
        point_no: pointNo,
        series_no: 2,
        direction: 'ASC',
        setpoint,
        pattern_reading: toNum(r.s2_asc_pattern, NaN),
        instrument_reading: toNum(r.s2_asc_instrument, NaN),
        unit,
      },
      {
        session_id: sessionId,
        point_no: pointNo,
        series_no: 2,
        direction: 'DESC',
        setpoint,
        pattern_reading: toNum(r.s2_desc_pattern, NaN),
        instrument_reading: toNum(r.s2_desc_instrument, NaN),
        unit,
      }
    );
  });

  const validPayload = payload.filter(
    (x) => Number.isFinite(x.pattern_reading) && Number.isFinite(x.instrument_reading)
  );

  if (!validPayload.length) return [];
  const { data, error } = await supabase
    .from('pressure_calibration_points')
    .upsert(validPayload, { onConflict: 'session_id,point_no,series_no,direction' })
    .select('*');

  if (error) throw error;
  return data || [];
}

export async function listPressureManometerPoints(sessionId) {
  const { data, error } = await supabase
    .from('pressure_calibration_points')
    .select('*')
    .eq('session_id', sessionId)
    .order('point_no', { ascending: true })
    .order('series_no', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function calculatePressureManometerResults(sessionId) {
  const [session, points] = await Promise.all([
    getPressureManometerSession(sessionId),
    listPressureManometerPoints(sessionId),
  ]);

  const grouped = new Map();
  (points || []).forEach((p) => {
    const arr = grouped.get(p.point_no) || [];
    arr.push(p);
    grouped.set(p.point_no, arr);
  });

  const out = [];
  const tempCorrectionFactor = computeTemperatureCorrectionFactor({
    ambientTemp: session?.ambient_temp_avg ?? session?.ambient_temp ?? null,
    instrumentUrl: session?.instrument_url ?? null,
  });

  for (const [pointNo, rows] of grouped.entries()) {
    const metrics = computePressurePointMetrics(rows, { ...session, temp_correction_factor: tempCorrectionFactor });
    if (!metrics) continue;

    // temp_correction_factor es un valor de la SESIÓN (ya se guarda en la sesión):
    // la tabla de resultados NO tiene esa columna, y si se manda PostgREST rechaza
    // todo el upsert con 400 (PGRST204). Se excluye del registro por punto.
    const { temp_correction_factor: _omitTcf, ...pointMetrics } = metrics;
    out.push({
      session_id: sessionId,
      point_no: pointNo,
      ...pointMetrics,
    });
  }

  if (!out.length) return [];
  const { data, error } = await supabase
    .from('pressure_calibration_results')
    .upsert(out, { onConflict: 'session_id,point_no' })
    .select('*')
    .order('point_no', { ascending: true });

  if (error) throw error;

  await updatePressureManometerSession(sessionId, {
    status: 'calculated',
    temp_correction_factor: tempCorrectionFactor,
  });
  return data || [];
}

export async function listPressureManometerResults(sessionId) {
  const { data, error } = await supabase
    .from('pressure_calibration_results')
    .select('*')
    .eq('session_id', sessionId)
    .order('point_no', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function closePressureManometerSession(sessionId, { certificateNumber = null, certificateUrl = null } = {}) {
  const patch = {
    status: 'closed',
    certificate_created_at: new Date().toISOString(),
  };
  if (certificateNumber) patch.certificate_number = certificateNumber;
  if (certificateUrl) patch.certificate_url = certificateUrl;
  return updatePressureManometerSession(sessionId, patch);
}






